import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { contactService } from "../../services/crm/contactService";
import { createCachedEntityHooks } from "../createEntityHooks";
import { FileMetadataDTO } from "../../interfaces/DTO/storageFile";

// ✅ hook-ים גנריים עם cache לפי id בלבד וממוינים לפי updated_at
export const {
  useAll: useContacts,
  useEntityById: useContactById,
  useEntityByIds: useContactByIds,
  useCreateEntity: useCreateContact,
  useUpdateEntity: useUpdateContact,
  usePartialUpdateEntity: usePartialUpdateContact,
  useDeleteEntity: useDeleteContact,
} = createCachedEntityHooks<Contact>({
  entityName: "contacts",
  service: contactService,
  enableCacheById: true,
  staleTimeMs: 60000, // 60 שניות - כדי שלא יהיה re-fetch מיותר
  defaultOrderBy: "updated_at",
  defaultOrderDesc: true,
});

export function useInvalidateContacts() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["contacts"] });
}

export function useContactFiles(contactId: number) {
  return useQuery({
    queryKey: ["contact-files", contactId],
    queryFn: () => contactService.listFiles(contactId),
  });
}

export function useContactInsuranceFiles(contactId: number | undefined) {
  return useQuery({
    queryKey: ["contact-insurance-files", contactId],
    queryFn: () => contactService.listInsuranceFiles(contactId!),
    enabled: !!contactId,
  });
}
export function useUpdateContactFileMetadata(contactId: number) {
  return useMutation({
    mutationFn: ({
      full_path,
      metadata,
    }: {
      full_path: string;
      metadata: FileMetadataDTO;
    }) => contactService.updateFileMetadata(contactId, full_path, metadata),
  });
}
export function useUploadContactFile(contactId: number) {
  return useMutation({
    mutationFn: ({
      file,
      customName,
      metadata,
    }: {
      file: File;
      customName?: string;
      metadata?: FileMetadataDTO;
    }) => contactService.uploadFile(contactId, file, customName, metadata),
  });
}
export function useDownloadContactFile(contactId: number) {
  return useMutation({
    mutationFn: async ({ full_path }: { full_path: string }) => {
      return await contactService.getContactFileStreamUrl(contactId, full_path);
    },
  });
}

export function useDeleteContactFile(contactId: number) {
  return useMutation({
    mutationFn: ({ full_path }: { full_path: string }) =>
      contactService.deleteFile(contactId, full_path),
  });
}

// Generic document extraction (supports all document types)
export function useExtractDocument(contactId: number) {
  return useMutation({
    mutationFn: ({ subject_ref }: { subject_ref: string }) =>
      contactService.extractDocument(contactId, subject_ref),
  });
}

// Get existing artifact for a document (supports all document types)
export function useGetDocumentArtifact(contactId: number) {
  return useMutation({
    mutationFn: ({ subject_ref }: { subject_ref: string }) =>
      contactService.getDocumentArtifact(contactId, subject_ref),
  });
}

// Get supported document types for AI extraction
export function useSupportedDocumentTypes(contactId: number) {
  return useQuery({
    queryKey: ["supportedDocumentTypes", contactId],
    queryFn: () => contactService.getSupportedDocumentTypes(contactId),
  });
}